import { getFullImageUrl, getThumbnailUrl } from "../../utils/urlConfig";
import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "react-modal";
import { fetchAssetDetailsById, assignLocationToAsset, assignParentAsset, assignSpareOfAsset, fetchAllAssets } from "../../services/assetService";
import { uploadAssetImage, setPrimaryAssetImage, deleteAssetImage, updateAsset } from "../../services/assetService";
import TagService from "../../services/TagService";
import AssetTagService from "../../services/AssetTagService";
import Select from "react-select";
import CameraCapture from "../CameraCapture/CameraCapture";
import QrScanner from "../Qr/QrScanner";
import styles from "./AssetModal.module.css"; // Import the CSS module
import piexif from "piexifjs";
import { FaUpload, FaCamera, FaBoxes, FaStar, FaTrash, FaEdit, FaSave, FaTimes, FaCopy, FaCheck, FaShareAlt, FaExternalLinkAlt, FaQrcode, FaTag, FaHistory } from "react-icons/fa";
import QRCode from "react-qr-code";
import ActivityHistoryModal from '../Activity/ActivityHistoryModal';

/**
 * AssetModal Component
 * Displays asset details and allows actions like uploading images, scanning QR codes, and assigning locations.
 * 
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Function to close the modal.
 * @param {string} assetId - The ID of the asset to display.
 * @param {function} refreshAssets - Function to refresh the list of assets after actions.
 */
const AssetModal = ({ isOpen, onClose, assetId, refreshAssets }) => {
  const [assetDetails, setAssetDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const streamRef = useRef(null); // Stream ref
  const [imageSrc, setImageSrc] = useState("");
  // capture image
  const [image, setImage] = useState(null);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  // Image edit
  const [showOverlay, setShowOverlay] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [copied, setCopied] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [allAssets, setAllAssets] = useState([]);
  const [qrScanningSpareOf, setQrScanningSpareOf] = useState(false);
  const spareOfStreamRef = useRef(null);

  // Tags State
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [pendingTag, setPendingTag] = useState(null);
  const [isTagsLoading, setIsTagsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTags();
    }
  }, [isOpen]);

  const loadTags = async () => {
    try {
      const tags = await TagService.getAllTags();
      setAvailableTags(tags.map(t => ({ value: t.id, label: t.name, color: t.color })));
    } catch (err) {
      console.error("Failed to load available tags", err);
    }
  };

  const loadAssetTags = async (id) => {
    if (!id || id === 'undefined') return;
    setIsTagsLoading(true);
    try {
      // First try to load from the asset object directly if the backend returns it
      if (assetDetails && assetDetails.tags) {
        const formattedSelected = assetDetails.tags.map(t => ({
          value: t.id,
          label: t.name,
          color: t.color,
          // We don't have junction ID here yet if we use TagResponse, 
          // but we can use removeAssetTag fallback
        }));
        setSelectedTags(formattedSelected);
      }

      // Refresh from relationship table to get junction IDs for easier deletion
      const assetTagsData = await AssetTagService.getAssetTagsByAssetId(id);
      
      const formattedSelected = assetTagsData.map(at => ({
        value: at.tag.id,
        label: at.tag.name,
        color: at.tag.color,
        assetTagId: at.id // Keep the junction ID so we can delete it later
      }));
      setSelectedTags(formattedSelected);
      
    } catch (err) {
        console.error("Failed to load tags for this asset", err);
    } finally {
        setIsTagsLoading(false);
    }
  };


  useEffect(() => {
    if (assetDetails) {
      setEditFormData({
        name: assetDetails.name,
        description: assetDetails.description,
        // Identification
        serialNo: assetDetails.serialNo === -1 ? "" : assetDetails.serialNo, // Handle legacy -1
        modelNo: assetDetails.modelNo === -1 ? "" : assetDetails.modelNo, // Handle legacy -1
        manufacturer: assetDetails.manufacturer || "",
        notes: assetDetails.notes || "",
        // Status
        insured: assetDetails.insured === 1,
        archived: assetDetails.archived === 1,
        damaged: assetDetails.damaged === 1,
        // Purchase
        purchaseStore: assetDetails.purchasedDetails?.storeName || "",
        purchaseDate: assetDetails.purchasedDetails?.purchasedDate || "",
        purchasePrice: assetDetails.purchasedDetails?.purchasedPrice || "",
        purchaseCountry: assetDetails.purchasedDetails?.purchasedCountry || "",
        warrantyExpiresOn: assetDetails.warrantyDetails?.warrantyExpiresOn || "",
        warrantyNotes: assetDetails.warrantyDetails?.warrantyNotes || "",
        lifetimeWarranty: assetDetails.warrantyDetails?.lifetimeWarranty || false,
        soldTo: assetDetails.soldDetails?.soldTo || "",
        soldDate: assetDetails.soldDetails?.soldAt || "",
        soldPrice: assetDetails.soldDetails?.soldPrice || "",
        spareOfId: assetDetails.spareOf?.id || ""
      });
      loadAssetTags(assetDetails.id);
    }
  }, [assetDetails]);

  useEffect(() => {
    if (isEditing && allAssets.length === 0) {
      fetchAllAssets().then(data => {
        setAllAssets(data.filter(a => a.id !== parseInt(assetId)));
      }).catch(err => console.error("Error fetching assets", err));
    }
  }, [isEditing, allAssets.length, assetId]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form data
    if (assetDetails) {
      setEditFormData({
        name: assetDetails.name,
        description: assetDetails.description,
        serialNo: assetDetails.serialNo === -1 ? "" : assetDetails.serialNo,
        modelNo: assetDetails.modelNo === -1 ? "" : assetDetails.modelNo,
        manufacturer: assetDetails.manufacturer || "",
        notes: assetDetails.notes || "",
        insured: assetDetails.insured === 1,
        archived: assetDetails.archived === 1,
        damaged: assetDetails.damaged === 1,
        purchaseStore: assetDetails.purchasedDetails?.storeName || "",
        purchaseDate: assetDetails.purchasedDetails?.purchasedDate || "",
        purchasePrice: assetDetails.purchasedDetails?.purchasedPrice || "",
        purchaseCountry: assetDetails.purchasedDetails?.purchasedCountry || "",
        warrantyExpiresOn: assetDetails.warrantyDetails?.warrantyExpiresOn || "",
        warrantyNotes: assetDetails.warrantyDetails?.warrantyNotes || "",
        lifetimeWarranty: assetDetails.warrantyDetails?.lifetimeWarranty || false,
        soldTo: assetDetails.soldDetails?.soldTo || "",
        soldDate: assetDetails.soldDetails?.soldAt || "",
        soldPrice: assetDetails.soldDetails?.soldPrice || "",
        spareOfId: assetDetails.spareOf?.id || ""
      });
      loadAssetTags(assetDetails.id);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTagsChange = (option, actionMeta) => {
    // Just update pending state, don't save yet
    setPendingTag(option);
  };

  const handleAddTag = async () => {
    if (!pendingTag || !assetId) return;
    
    // Check if duplicate
    if (selectedTags.find(t => t.value === pendingTag.value)) {
        toast.info("Tag already assigned");
        setPendingTag(null);
        return;
    }

    try {
      const result = await AssetTagService.addAssetTag({
        asset: { id: assetId },
        tag: { id: pendingTag.value }
      });
      
      const tagWithId = { ...pendingTag, assetTagId: result.id };
      setSelectedTags([...selectedTags, tagWithId]);
      setPendingTag(null);
      toast.success(`Added tag: ${pendingTag.label}`);
      
      // Update parent only if needed, but don't force a full page refresh feel
      if(refreshAssets) refreshAssets();
    } catch (err) {
      toast.error("Failed to add tag");
      console.error(err);
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    try {
      if (tagToRemove.assetTagId) {
        await AssetTagService.deleteAssetTag(tagToRemove.assetTagId);
      } else {
        await AssetTagService.removeAssetTag(assetId, tagToRemove.value);
      }
      
      setSelectedTags(selectedTags.filter(t => t.value !== tagToRemove.value));
      toast.success(`Removed tag: ${tagToRemove.label}`);
      if(refreshAssets) refreshAssets();
    } catch (err) {
      toast.error("Failed to remove tag");
      console.error(err);
    }
  };

  const customReactSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: 'var(--card-bg-color, #fff)',
      borderColor: 'var(--border-color, #ccc)',
      color: 'var(--text-color, #333)',
      minHeight: '38px',
      borderRadius: '6px'
    }),
    multiValue: (styles, { data }) => {
      return {
        ...styles,
        backgroundColor: data.color ? `${data.color}33` : '#3b82f633',
        borderRadius: '6px',
        border: `1px solid ${data.color || '#3b82f6'}`
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: 'var(--text-color)',
      fontWeight: '600',
      fontSize: '12px'
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: data.color || '#3b82f6',
      ':hover': {
        backgroundColor: data.color || '#3b82f6',
        color: 'white',
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'var(--card-bg-color, #fff)',
      zIndex: 999
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected 
        ? 'var(--primary-color)' 
        : isFocused 
          ? 'rgba(59, 130, 246, 0.1)' 
          : 'var(--card-bg-color)',
      color: isSelected ? 'white' : 'var(--text-color)'
    })
  };

  const handleSaveEdit = async () => {
    try {
      // Prepare payload - ensure numbers are numbers
      const payload = {
        ...editFormData,
        insured: editFormData.insured ? 1 : 0,
        archived: editFormData.archived ? 1 : 0,
        damaged: editFormData.damaged ? 1 : 0,
        purchasePrice: editFormData.purchasePrice ? parseFloat(editFormData.purchasePrice) : null,
        soldPrice: editFormData.soldPrice ? parseFloat(editFormData.soldPrice) : null
      };

      const res = await updateAsset(assetId, payload);
      if (res) {
        // If spareOfId changed, update it separately
        if (editFormData.spareOfId !== (assetDetails.spareOf?.id || "")) {
          const sid = editFormData.spareOfId === "" ? -1 : editFormData.spareOfId;
          await assignSpareOfAsset(assetId, sid);
        }
        const updatedRes = await fetchAssetDetailsById(assetId);
        setAssetDetails(updatedRes.data);
        setIsEditing(false);
        toast.success("Asset updated successfully!");
        if(refreshAssets) refreshAssets();
      }
    } catch (err) {
      console.error("Failed to update asset", err);
      toast.error("Failed to update asset.");
    }
  };

  const copyToClipboard = () => {
    const identifier = assetDetails?.qrToken || assetId;
    const publicUrl = `${window.location.origin}/public/assets/${identifier}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied to clipboard");
    });
  };

  /**
   * Handles asset image loading errors.
   * Sets the image source to empty when an error occurs.
   */
  const handleImageError = () => {
    setImageSrc(""); // Set to empty string to show the frame
  };

  /**
   * Handles multiple image selection and uploads them to the server.
   * 
   * @param {Event} event - The file input change event.
   */
  const handleSetPrimary = async (imageId) => {
    try {
      await setPrimaryAssetImage(imageId);
      const updatedRes = await fetchAssetDetailsById(assetId);
      setAssetDetails(updatedRes.data);
      // Find the new primary image path to update view
      const newPrimary = updatedRes.data.imageDetails?.find(img => img.isPrimary);
      if (newPrimary) setImageSrc(newPrimary.imagePath);
      toast.success("Primary image updated!");
    } catch (err) {
      console.error("Error setting primary image", err);
      toast.error("Failed to set primary image");
    }
  };

  const handleUnlinkLocation = async () => {
    if (!window.confirm("Are you sure you want to remove this asset from its current container?")) return;
    try {
      await assignLocationToAsset(assetId, -1);
      const updatedRes = await fetchAssetDetailsById(assetId);
      setAssetDetails(updatedRes.data);
      toast.success("Location unlinked successfully!");
    } catch (err) {
      console.error("Error unlinking location", err);
      toast.error("Failed to unlink location");
    }
  };

  // const handleUnlinkParentAsset = async () => {
  //   if (!window.confirm("Are you sure you want to remove this asset from its parent association?")) return;
  //   try {
  //     await assignParentAsset(assetId, -1);
  //     const updatedRes = await fetchAssetDetailsById(assetId);
  //     setAssetDetails(updatedRes.data);
  //     toast.success("Parent asset unlinked successfully!");
  //   } catch (err) {
  //     console.error("Error unlinking parent asset", err);
  //     toast.error("Failed to unlink parent asset");
  //   }
  // };

  const handleUnlinkSpareOfAsset = async () => {
    if (!window.confirm("Are you sure you want to remove this asset from its spare of association?")) return;
    try {
      await assignSpareOfAsset(assetId, -1);
      const updatedRes = await fetchAssetDetailsById(assetId);
      setAssetDetails(updatedRes.data);
      toast.success("SpareOf asset unlinked successfully!");
    } catch (err) {
      console.error("Error unlinking spareOf asset", err);
      toast.error("Failed to unlink spareOf asset");
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    try {
      await deleteAssetImage(imageId);
      const updatedRes = await fetchAssetDetailsById(assetId);
      setAssetDetails(updatedRes.data);
      if (updatedRes.data.images && updatedRes.data.images.length > 0) {
        setImageSrc(updatedRes.data.images[0]);
      } else {
        setImageSrc("");
      }
      toast.success("Image deleted successfully!");
    } catch (err) {
      console.error("Error deleting image", err);
      toast.error("Failed to delete image");
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      return;
    }

    // Validate files
    const validFiles = Array.from(files).filter((file) => file.type.startsWith("image"));
    if (validFiles.length === 0) {
      toast.error("Please upload valid image files.");
      return;
    }

    try {
      const result = await uploadAssetImage(assetId, validFiles);
      if (result.success) {
        toast.success("Images uploaded successfully!");
        console.log("Images uploaded successfully:", result.data);
        // Refresh asset details to get new images
        const updatedRes = await fetchAssetDetailsById(assetId);
        setAssetDetails(updatedRes.data);
        // Update to show the newly added image (last one) or primary
        if (updatedRes.data.images && updatedRes.data.images.length > 0) {
          // Prefer primary, else last
          const primary = updatedRes.data.imageDetails?.find(img => img.isPrimary);
          setImageSrc(primary ? primary.imagePath : updatedRes.data.images[updatedRes.data.images.length - 1]);
        }
      } else {
        toast.error(`Image upload failed: ${result.error}`);
        console.error("Image upload failed:", result.error);
      }
    } catch (error) {
      console.error("Error while uploading images:", error);
      toast.error("An error occurred during image upload.");
    }
  };

  // Convert Base64 (PNG) to JPEG
  const convertToJpegBase64 = async (base64Png) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Png;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg")); // Convert PNG → JPEG
      };
    });
  };

  // Convert Base64 to File
  const base64ToFile = (base64String, fileName) => {
    const arr = base64String.split(",");
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: "image/jpeg" });
  };

  // Convert Decimal Degrees to EXIF GPS Format
  const convertToExifGps = (coord) => {
    const absCoord = Math.abs(coord);
    const degrees = Math.floor(absCoord);
    const minutes = Math.floor((absCoord - degrees) * 60);
    const seconds = ((absCoord - degrees - minutes / 60) * 3600 * 1000).toFixed(0);
    return [[degrees, 1], [minutes, 1], [seconds, 1000]];
  };

  // Embed GPS Metadata in JPEG EXIF
  const embedGpsMetadata = (jpegBase64, location) => {
    let exifObj = { "GPS": {} };

    if (location.latitude && location.longitude) {
      exifObj.GPS[piexif.GPSIFD.GPSLatitude] = convertToExifGps(location.latitude);
      exifObj.GPS[piexif.GPSIFD.GPSLatitudeRef] = location.latitude >= 0 ? "N" : "S";
      exifObj.GPS[piexif.GPSIFD.GPSLongitude] = convertToExifGps(location.longitude);
      exifObj.GPS[piexif.GPSIFD.GPSLongitudeRef] = location.longitude >= 0 ? "E" : "W";
    }

    // Insert EXIF into JPEG Base64
    const exifBytes = piexif.dump(exifObj);
    return piexif.insert(exifBytes, jpegBase64);
  };

  // Upload Image with Location Metadata
  const uploadImage = async () => {
    if (!image || !location.latitude || !location.longitude) {
      toast.warning("Please capture an image and allow location access first.");
      return;
    }

    try {
      const jpegBase64 = await convertToJpegBase64(image);
      const modifiedBase64 = embedGpsMetadata(jpegBase64, location);
      const imageFile = base64ToFile(modifiedBase64, "captured_image_with_location.jpg");

      const result = await uploadAssetImage(assetId, [imageFile]);
      if (result.success) {
        toast.success("Image uploaded successfully with location metadata!");
        const updatedRes = await fetchAssetDetailsById(assetId);
        setAssetDetails(updatedRes.data);
        if (updatedRes.data.images && updatedRes.data.images.length > 0) {
          setImageSrc(updatedRes.data.images[updatedRes.data.images.length - 1]);
        }
      } else {
        toast.error("Upload failed.");
      }
    } catch (error) {
      console.error("Error uploading captured image:", error);
      toast.error("An error occurred during upload.");
    }
  };

  /**
   * Updates the stream reference when a new stream is provided.
   * @param {MediaStream} stream - The video stream used for QR scanning.
   */
  const handleSpareOfScan = (result) => {
    if (result) {
      const itemId = getLastValueFromUrl(result);
      if (itemId) {
        // Check if ID exists in allAssets
        const exists = allAssets.some(a => a.id === parseInt(itemId));
        if (exists) {
          setEditFormData(prev => ({
            ...prev,
            spareOfId: itemId
          }));
          setQrScanningSpareOf(false);
          stopSpareOfCamera();
          toast.success(`Linked to Asset ID: ${itemId}`);
        } else {
          toast.error(`Asset ID ${itemId} not found in available assets.`);
        }
      }
    }
  };

  const getLastValueFromUrl = (url) => {
    if (!url) return "";
    const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    const segments = cleanUrl.split('/');
    return segments[segments.length - 1];
  };

  const stopSpareOfCamera = () => {
    if (spareOfStreamRef.current) {
      const tracks = spareOfStreamRef.current.getTracks();
      tracks.forEach((track) => track.stop());
      spareOfStreamRef.current = null;
    }
  };

  /**
   * Updates the stream reference when a new stream is provided.
   * @param {MediaStream} stream - The video stream used for QR scanning.
   */
  // const setStreamParent = (stream) => {
  //   streamRef.current = stream; // Update stream reference
  // };

  /**
   * Stops the camera by stopping each track in the stream.
   */
  const stopCamera = () => {
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach((track) => track.stop());
      streamRef.current = null; // Clear the reference
    }
  };

  // Fetch asset details when the modal opens or assetId changes
  useEffect(() => {
    if (isOpen && assetId && assetId !== 'undefined') {
      setAssetDetails(null);
      setImageSrc("");
      setLoading(true);
      fetchAssetDetailsById(assetId)
        .then((res) => {
          setAssetDetails(res.data);
          if (res.data.images && res.data.images.length > 0) {
            setImageSrc(res.data.images[0]);
          } else {
            setImageSrc("");
          }
        })
        .catch((err) => {
          console.error("Error fetching asset details:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    return () => {
      stopCamera();
      stopSpareOfCamera();
    };
  }, [isOpen, assetId]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        stopCamera();
        stopSpareOfCamera();
        onClose();
      }}
      className={styles.modal}
      overlayClassName={styles["modal-overlay"]}
    >
      <div className={styles["modal-header"]}>
        <h2>Asset Details</h2>
        <div className={styles.headerActions}>
          {!isEditing && (
            <button className={styles.iconButton} onClick={handleEditClick} title="Edit Asset">
              <FaEdit />
            </button>
          )}
          {isEditing && (
            <>
              <button className={styles.iconButton} onClick={handleSaveEdit} title="Save Changes">
                <FaSave />
              </button>
              <button className={styles.iconButton} onClick={handleCancelEdit} title="Cancel Edit">
                <FaTimes />
              </button>
            </>
          )}
          <button
            className={styles["close-button"]}
            onClick={() => {
              stopCamera();
              stopSpareOfCamera();
              onClose();
            }}
          >
            &times;
          </button>
        </div>
      </div>

      <div className={styles["modal-content"]}>
        {loading ? (
          <p className={styles.loading}>Loading asset details...</p>
        ) : assetDetails ? (
          <div className={styles["flex-row"]}>
            {/* Left Column: Visual Identity (Sticky) */}
            <div className={styles["flex-column"]}>
              <div className={styles["visual-identity"]}>
                <div className={styles["image-frame"]}>
                  {imageSrc ? (
                    <img
                      src={getFullImageUrl(imageSrc)}
                      alt="Asset"
                      className={styles["asset-image"]}
                      onError={handleImageError}
                    />
                  ) : (
                    <div className={styles["no-image"]}>No image available</div>
                  )}

                  <div className={styles.overlay}>
                    <button
                      className={styles.iconButton}
                      onClick={() => setShowCamera(true)}
                      title="Capture Image"
                    >
                      <FaCamera />
                    </button>

                    <label htmlFor="file-upload" className={styles.iconButton} title="Upload Images">
                      <FaUpload />
                      <input
                        type="file"
                        id="file-upload"
                        multiple
                        accept="image/*"
                        className={styles.hiddenInput}
                        onChange={handleImageUpload}
                      />
                    </label>
                  </div>
                </div>

                {/* Image Gallery with Management */}
                {assetDetails?.imageDetails && assetDetails.imageDetails.length > 0 ? (
                  <div className={styles["image-gallery"]}>
                    {assetDetails.imageDetails.map((img) => (
                      <div key={img.id} className={`${styles.galleryItemWrapper} ${imageSrc === img.imagePath ? styles.activeWrapper : ""}`}>
                        <img
                          src={getThumbnailUrl(img.thumbnailPath || img.imagePath)}
                          alt={`Thumbnail ${img.id}`}
                          className={styles["gallery-thumbnail"]}
                          onClick={() => setImageSrc(img.imagePath)}
                        />
                        <div className={styles.galleryControls}>
                          <button
                            className={`${styles.controlBtn} ${img.isPrimary ? styles.primaryActive : ""}`}
                            onClick={(e) => { e.stopPropagation(); handleSetPrimary(img.id); }}
                            title="Set as Main Image"
                          >
                            <FaStar />
                          </button>
                          <button
                            className={styles.controlBtn}
                            onClick={(e) => { e.stopPropagation(); handleDeleteImage(img.id); }}
                            title="Delete Image"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Fallback for legacy assets without imageDetails populated yet */
                  assetDetails?.images && assetDetails.images.length > 0 && (
                    <div className={styles["image-gallery"]}>
                      {assetDetails.images.map((img, index) => (
                        <img
                          key={index}
                          src={getThumbnailUrl(img)}
                          alt={`Asset ${index}`}
                          className={`${styles["gallery-thumbnail"]} ${imageSrc === img ? styles["active-thumbnail"] : ""}`}
                          onClick={() => setImageSrc(img)}
                        />
                      ))}
                    </div>
                  )
                )}

                {/* Public Share Link Container */}
                <div className={styles["share-container"]}>
                  <span className={styles["qr-label"]}><FaShareAlt style={{ marginRight: '6px' }} /> Public Share Link</span>
                  <div className={styles.shareUrlWrapper}>
                    <input
                      type="text"
                      readOnly
                      className={styles.shareInput}
                      value={`${window.location.origin}/public/assets/${assetDetails?.qrToken || assetId}`}
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      className={`${styles.copyBtn} ${copied ? styles.copied : ""}`}
                      onClick={copyToClipboard}
                      title="Copy Link"
                    >
                      {copied ? <FaCheck /> : <FaCopy />}
                    </button>
                    <a
                      href={`${window.location.origin}/public/assets/${assetDetails?.qrToken || assetId}`}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.externalBtn}
                      title="Open Public Page"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  </div>
                </div>

                {/* QR Code Container */}
                {assetDetails?.qrToken ? (
                  <div className={styles["qr-container"]}>
                    <span className={styles["qr-label"]}>Registered QR Token</span>
                    <div style={{ background: 'white', padding: '10px', borderRadius: '8px', marginTop: '10px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
                      <QRCode 
                        value={`${window.location.origin}/scan/${assetDetails.qrToken}`} 
                        size={150} 
                        style={{ maxWidth: '100%', height: 'auto' }}
                      />
                      <div style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '8px', color: 'var(--primary-color)' }}>
                        {assetDetails.qrToken}
                      </div>
                    </div>
                  </div>
                ) : assetDetails?.qrCode && (
                  <div className={styles["qr-container"]}>
                    <span className={styles["qr-label"]}>Asset ID Mapping</span>
                    <img
                      src={getFullImageUrl(assetDetails.qrCode)}
                      alt="QR Code"
                      className={styles["qr-image"]}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Detailed Information */}
            <div className={styles["flex-column-details"]}>
              <div className={styles["asset-info"]}>
                {isEditing ? (
                  <>
                    <input className={styles.editInputTitle} name="name" value={editFormData.name} onChange={handleChange} placeholder="Asset Name" />
                    <textarea className={styles.editInputDesc} name="description" value={editFormData.description} onChange={handleChange} placeholder="Description" rows={3} />
                  </>
                ) : (
                  <>
                    <h3>{assetDetails?.name || "-"}</h3>
                    <p className={styles.description}>{assetDetails?.description || "-"}</p>
                  </>
                )}

                {/* Identification Card */}
                <div className={styles["details-section"]}>
                  <h4>Identification</h4>
                  <div className={styles["info-grid"]}>
                    <div className={styles["info-item"]}>
                      <strong>Asset ID</strong>
                      <span className={styles.value}>{assetDetails?.assetId || "-"}</span>
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Serial Number</strong>
                      {isEditing ? <input className={styles.editInput} name="serialNo" value={editFormData.serialNo} onChange={handleChange} placeholder="Serial No" /> : <span className={styles.value}>{(!assetDetails?.serialNo || assetDetails?.serialNo === -1) ? "-" : assetDetails?.serialNo}</span>}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Model Number</strong>
                      {isEditing ? <input className={styles.editInput} name="modelNo" value={editFormData.modelNo} onChange={handleChange} placeholder="Model No" /> : <span className={styles.value}>{(!assetDetails?.modelNo || assetDetails?.modelNo === -1) ? "-" : assetDetails?.modelNo}</span>}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Manufacturer</strong>
                      {isEditing ? <input className={styles.editInput} name="manufacturer" value={editFormData.manufacturer} onChange={handleChange} placeholder="Manufacturer" /> : <span className={styles.value}>{assetDetails?.manufacturer || "-"}</span>}
                    </div>
                  </div>
                </div>

                {/* Status & Ownership Card */}
                <div className={styles["details-section"]}>
                  <h4>Status & Ownership</h4>
                  <div className={styles["info-grid"]}>
                    <div className={styles["info-item"]}>
                      <strong>Assigned Owner</strong>
                      <span className={styles.value}>{assetDetails?.user?.name || "-"}</span>
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Insured Coverage</strong>
                      {isEditing ? (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                          <input type="checkbox" name="insured" checked={editFormData.insured} onChange={handleChange} />
                          Covered
                        </label>
                      ) : (
                        <span className={styles.value}>{assetDetails?.insured === 1 ? "Yes" : "No"}</span>
                      )}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Archive Status</strong>
                      {isEditing ? (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                          <input type="checkbox" name="archived" checked={editFormData.archived} onChange={handleChange} />
                          Archived
                        </label>
                      ) : (
                        <span className={styles.value}>{assetDetails?.archived === 1 ? "Archived" : "Active"}</span>
                      )}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Condition</strong>
                      {isEditing ? (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                          <input type="checkbox" name="damaged" checked={editFormData.damaged} onChange={handleChange} />
                          Damaged
                        </label>
                      ) : (
                        <span className={styles.value}>{assetDetails?.damaged === 1 ? "Damaged" : "Excellent"}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tags / Collections Card */}
                <div className={styles["details-section"]}>
                  <h4>Tags & Collections</h4>
                  <div style={{ marginTop: '10px' }}>
                    {isEditing ? (
                      <div className={styles.tagsEditContainer}>
                        <div className={styles.tagsControlRow}>
                          <div style={{ flex: 1 }}>
                            <Select
                              name="tags"
                              options={availableTags}
                              className="basic-single"
                              classNamePrefix="select"
                              value={pendingTag}
                              onChange={handleTagsChange}
                              styles={customReactSelectStyles}
                              placeholder={isTagsLoading ? "Loading..." : "Select a tag..."}
                              isLoading={isTagsLoading}
                              isClearable
                            />
                          </div>
                          <button 
                            type="button" 
                            className={styles.addTagBtn} 
                            onClick={handleAddTag}
                            disabled={!pendingTag || isTagsLoading}
                          >
                            <FaCheck /> Add
                          </button>
                        </div>
                        
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                          {selectedTags.map(tag => (
                            <span 
                              key={tag.value} 
                              style={{ 
                                backgroundColor: tag.color ? `${tag.color}33` : '#e2e8f0',
                                border: `1px solid ${tag.color || '#cbd5e1'}`,
                                color: 'var(--text-color)',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              {tag.label}
                              <FaTimes 
                                style={{ cursor: 'pointer', color: '#ef4444' }} 
                                onClick={() => handleRemoveTag(tag)} 
                                title="Remove Tag"
                              />
                            </span>
                          ))}
                        </div>
                        <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>Tags are saved immediately when added.</small>
                      </div>
                    ) : (
                      <div className={styles.tagsViewContainer}>
                        {isTagsLoading ? (
                          <span className={styles.value}>Loading tags...</span>
                        ) : selectedTags.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {selectedTags.map(tag => (
                              <span 
                                key={tag.value} 
                                style={{ 
                                  backgroundColor: tag.color ? `${tag.color}33` : '#e2e8f0',
                                  border: `1px solid ${tag.color || '#cbd5e1'}`,
                                  color: 'var(--text-color)',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {tag.color && <FaTag style={{ color: tag.color, fontSize: '10px' }} />}
                                {tag.label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={styles.value} style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>No tags assigned.</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Container & Hierarchy Card */}
                <div className={styles["details-section"]}>
                  <h4>Container & Hierarchy</h4>
                  <div className={styles["info-grid"]}>
                    <div className={styles["info-item"]}>
                      <strong>Physical Container (Location)</strong>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className={styles.value}>{assetDetails?.location ? `${assetDetails.location.name} (${assetDetails.location.id})` : "-"}</span>
                        {assetDetails?.location && (
                          <button className={styles.unlinkInlineBtn} onClick={handleUnlinkLocation} title="Remove Location">
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Spare Of Association</strong>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {isEditing ? (
                          <div className={styles.spareOfEditWrapper}>
                            <div className={styles.spareOfSelectRow}>
                              <select
                                className={styles.editInput}
                                name="spareOfId"
                                value={editFormData.spareOfId}
                                onChange={handleChange}
                              >
                                <option value="">-- No Spare Association --</option>
                                {allAssets.map(a => (
                                  <option key={a.id} value={a.id}>{a.name} ({a.id})</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className={styles.scanIconBtn}
                                onClick={() => setQrScanningSpareOf(!qrScanningSpareOf)}
                                title="Scan QR Code or Type URL"
                              >
                                {qrScanningSpareOf ? <FaTimes /> : <FaQrcode />}
                              </button>
                            </div>

                            {qrScanningSpareOf && (
                              <div className={styles.spareOfScannerSection}>
                                <input
                                  type="text"
                                  className={styles.manualUrlInput}
                                  placeholder="Type Asset URL or ID here..."
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      handleSpareOfScan(e.target.value);
                                      e.target.value = "";
                                    }
                                  }}
                                  autoFocus
                                />
                                <div className={styles.qrScannerContainer}>
                                  <QrScanner
                                    onScan={handleSpareOfScan}
                                    onError={(err) => console.error("Scanner Error:", err)}
                                    setStreamParent={(stream) => { spareOfStreamRef.current = stream; }}
                                  />
                                </div>
                                <button
                                  type="button"
                                  className={styles.stopScanBtn}
                                  onClick={() => {
                                    stopSpareOfCamera();
                                    setQrScanningSpareOf(false);
                                  }}
                                >
                                  Close Scanner
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={styles.value}>{assetDetails?.spareOf?.name || "-"}</span>
                        )}
                        {assetDetails?.spareOf && !isEditing && (
                          <button className={styles.unlinkInlineBtn} onClick={handleUnlinkSpareOfAsset} title="Remove Spare Association">
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sub-Assets Card */}
                <div className={styles["details-section"]}>
                  <h4>Contained Assets</h4>
                  <div className={styles["content-list"]}>
                    {assetDetails.childAssets && assetDetails.childAssets.length > 0 ? (
                      assetDetails.childAssets.map((child) => (
                        <div key={child.id} className={styles["content-item"]}>
                          <div className={styles["item-name"]}>
                            <FaBoxes style={{ marginRight: '8px', color: 'var(--primary-color)' }} />
                            {child.name}
                          </div>
                          <div className={styles["item-type"]}>Asset ({child.id})</div>
                        </div>
                      ))
                    ) : (
                      <p style={{ fontStyle: 'italic', color: 'var(--text-muted)', margin: 0 }}>No sub-assets assigned to this item.</p>
                    )}
                  </div>
                </div>

                {/* Notes Card */}
                <div className={styles["details-section"]}>
                  <h4>Notes</h4>
                  {isEditing ? (
                    <textarea className={styles.editInput} name="notes" value={editFormData.notes} onChange={handleChange} rows={3} placeholder="Add notes here..." />
                  ) : (
                    <p className={styles.notesText}>{assetDetails?.notes || "-"}</p>
                  )}
                </div>
              </div>

              {/* Maintenance History */}
              {assetDetails?.maintenanceRecords?.length > 0 && (
                <div className={styles["details-section"]}>
                  <h4>Maintenance Records</h4>
                  <div className={styles["maintenance-list"]}>
                    {assetDetails.maintenanceRecords.map((m, i) => (
                      <div key={i} className={styles["maintenance-item"]}>
                        <div className={styles.mDetails}>
                          <strong>{m.scheduledDate || "-"}</strong>
                          <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>
                            {m.isBattery ? "Battery Sync" : "General Service"}
                          </span>
                        </div>
                        <div className={styles.mCost}>Cost: {m.cost ?? '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Purchase & Financial Details */}
              {(assetDetails?.purchasedDetails || isEditing) && (
                <div className={styles["details-section"]}>
                  <h4>Purchase Details</h4>
                  <div className={styles["info-grid"]}>
                    <div className={styles["info-item"]}>
                      <strong>Store</strong>
                      {isEditing ? <input className={styles.editInput} name="purchaseStore" value={editFormData.purchaseStore} onChange={handleChange} /> : <span className={styles.value}>{assetDetails?.purchasedDetails?.storeName || "-"}</span>}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Date</strong>
                      {isEditing ? <input className={styles.editInput} type="date" name="purchaseDate" value={editFormData.purchaseDate} onChange={handleChange} /> : <span className={styles.value}>{assetDetails?.purchasedDetails?.purchasedDate || "-"}</span>}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Price</strong>
                      {isEditing ? <input className={styles.editInput} type="number" step="0.01" name="purchasePrice" value={editFormData.purchasePrice} onChange={handleChange} /> : <span className={styles.value}>{assetDetails?.purchasedDetails?.purchasedPrice || "-"}</span>}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Country</strong>
                      {isEditing ? <input className={styles.editInput} name="purchaseCountry" value={editFormData.purchaseCountry} onChange={handleChange} /> : <span className={styles.value}>{assetDetails?.purchasedDetails?.purchasedCountry || "-"}</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Sold/Disposal Details */}
              {(assetDetails?.soldDetails || isEditing) && (
                <div className={styles["details-section"]}>
                  <h4>Disposal Details</h4>
                  <div className={styles["info-grid"]}>
                    <div className={styles["info-item"]}>
                      <strong>Purchaser</strong>
                      {isEditing ? <input className={styles.editInput} name="soldTo" value={editFormData.soldTo} onChange={handleChange} /> : <span className={styles.value}>{assetDetails?.soldDetails?.soldTo || "-"}</span>}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Date</strong>
                      {isEditing ? <input className={styles.editInput} type="date" name="soldDate" value={editFormData.soldDate} onChange={handleChange} /> : <span className={styles.value}>{assetDetails?.soldAt || "-"}</span>}
                    </div>
                    <div className={styles["info-item"]}>
                      <strong>Price</strong>
                      {isEditing ? <input className={styles.editInput} type="number" step="0.01" name="soldPrice" value={editFormData.soldPrice} onChange={handleChange} /> : <span className={styles.value}>{assetDetails?.soldDetails?.soldPrice || "-"}</span>}
                    </div>
                  </div>
                </div>
              )}

              {/* Protection & Warranty */}
              {(assetDetails?.warrantyDetails || isEditing) && (
                <div className={styles["details-section"]}>
                  <h4>Warranty Details</h4>
                  <div className={styles["info-grid"]}>
                    <div className={styles["info-item"]}>
                      <strong>Expiry Status</strong>
                      {isEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input className={styles.editInput} type="date" name="warrantyExpiresOn" value={editFormData.warrantyExpiresOn} onChange={handleChange} disabled={editFormData.lifetimeWarranty} />
                          <label style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" name="lifetimeWarranty" checked={editFormData.lifetimeWarranty} onChange={handleChange} /> Lifetime
                          </label>
                        </div>
                      ) : (
                        <span className={styles.value}>{assetDetails?.warrantyDetails?.warrantyExpiresOn || (assetDetails?.warrantyDetails?.lifetimeWarranty ? "Lifetime Protection" : "-")}</span>
                      )}
                    </div>
                  </div>
                  {(assetDetails?.warrantyDetails?.warrantyNotes || isEditing) && (
                    <div style={{ marginTop: '12px', fontSize: '13px', fontStyle: 'italic' }}>
                      <strong>Notes:</strong>
                      {isEditing ? <textarea className={styles.editInput} name="warrantyNotes" value={editFormData.warrantyNotes} onChange={handleChange} rows={2} /> : assetDetails?.warrantyDetails?.warrantyNotes}
                    </div>
                  )}
                </div>
              )}

              {!assetDetails?.parentAsset && !assetDetails?.maintenanceRecords?.length && !assetDetails?.purchasedDetails && !assetDetails?.soldDetails && !assetDetails?.warrantyDetails && (
                <p style={{ marginTop: '8px', fontStyle: 'italic', color: 'var(--text-muted)', textAlign: 'center' }}>
                  No historical or hierarchical records found for this asset.
                </p>
              )}
            </div>

            {showCamera && (
              <CameraCapture
                image={image}
                setImage={setImage}
                uploadImage={uploadImage}
                location={location}
                setLocation={setLocation}
                onClose={() => setShowCamera(false)}
              />
            )}
            
            {/* Asset History Button */}
            {!loading && assetDetails && (
              <div className={styles.historySection}>
                <button 
                  className={styles.viewHistoryBtn}
                  onClick={() => setHistoryModalOpen(true)}
                >
                  <FaHistory /> View Activity History
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className={styles.noData}>No details found for this asset.</p>
        )}
      </div>

      <ActivityHistoryModal 
        isOpen={historyModalOpen}
        onRequestClose={() => setHistoryModalOpen(false)}
        entityType="ASSET"
        entityId={assetId}
        entityName={assetDetails?.name}
      />
    </Modal>
  );
};

export default AssetModal;
